import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
    appinstalled: Event;
  }
}

export interface PWAInstallState {
  /** Whether the app can be installed (install prompt available) */
  canInstall: boolean;
  /** Whether the app is already installed */
  isInstalled: boolean;
  /** Whether running in standalone mode (installed PWA) */
  isStandalone: boolean;
  /** Platform-specific install instructions for iOS/other browsers */
  showIOSInstallHint: boolean;
  /** Trigger the native install prompt */
  promptInstall: () => Promise<void>;
  /** Dismiss the install prompt for this session */
  dismissPrompt: () => void;
}

const INSTALL_DISMISSED_KEY = "jumpbean-install-dismissed";
const INSTALL_DISMISSED_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Hook for PWA installation functionality.
 * Handles the "Add to Home Screen" prompt and tracks installation state.
 */
export function usePWAInstall(): PWAInstallState {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  // Check if running in standalone mode (already installed)
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window.navigator as any).standalone === true);

  // Detect iOS Safari (can't use beforeinstallprompt)
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !(window as any).MSStream;

  const isIOSSafari =
    isIOS &&
    /Safari/.test(navigator.userAgent) &&
    !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);

  // Check if install was recently dismissed
  const wasRecentlyDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!dismissedAt) return false;

    const dismissedTime = parseInt(dismissedAt, 10);
    return Date.now() - dismissedTime < INSTALL_DISMISSED_DURATION;
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent Chrome's default mini-infobar
      e.preventDefault();
      // Save the event for later use
      setDeferredPrompt(e);
      // Only show if not recently dismissed
      if (!wasRecentlyDismissed()) {
        setCanInstall(true);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      // Clear any dismissal state
      localStorage.removeItem(INSTALL_DISMISSED_KEY);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [isStandalone, wasRecentlyDismissed]);

  // Trigger the install prompt
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user's response
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === "accepted") {
      setIsInstalled(true);
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setCanInstall(false);
  }, [deferredPrompt]);

  // Dismiss the install prompt for this session
  const dismissPrompt = useCallback(() => {
    setCanInstall(false);
    localStorage.setItem(INSTALL_DISMISSED_KEY, Date.now().toString());
  }, []);

  // Show iOS install hint if on iOS Safari and not installed
  const showIOSInstallHint =
    isIOSSafari && !isStandalone && !wasRecentlyDismissed();

  return {
    canInstall,
    isInstalled,
    isStandalone,
    showIOSInstallHint,
    promptInstall,
    dismissPrompt,
  };
}

export default usePWAInstall;
